import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { apiSetAccountType } from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, Heart, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

export default function OnboardingPage() {
  const { token, refreshUser } = useAuth();
  const nav = useNavigate();

  const choose = async (type) => {
    try {
      await apiSetAccountType(token, type);
      await refreshUser();
      if (type === "per_me") {
        nav("/diary");
      } else {
        nav("/profile/create");
      }
      toast.success(type === "per_me" ? "Benvenuto nel tuo diario" : "Crea il profilo del tuo caro");
    } catch {
      toast.error("Errore");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-3xl">
        {/* Logo */}
        <div className="flex items-center gap-1 mb-4 justify-center">
          <span className="font-serif text-2xl font-bold text-foreground">Io</span>
          <span className="font-serif text-2xl font-bold text-accent">Ci</span>
          <span className="font-serif text-2xl font-bold text-foreground">Sono</span>
          <span className="text-xs text-muted-foreground font-sans ml-0.5">.org</span>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center mb-12">
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-4">Inizia qui</h1>
          <p className="text-base text-muted-foreground max-w-lg mx-auto">
            Questo account è per te o per rivivere un rapporto con una persona che non c'è più?
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 gap-6">
          {/* Per me */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
            <Card className="bg-card border-border/40 shadow-card hover:shadow-elevated transition-all duration-500 cursor-pointer group h-full" onClick={() => choose("per_me")}>
              <CardContent className="p-8 flex flex-col h-full">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/15 transition-colors duration-300">
                  <BookOpen className="w-7 h-7 text-primary" />
                </div>
                <h2 className="font-serif text-xl font-bold text-foreground mb-3">È per me</h2>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6 flex-1">
                  Crea il tuo diario digitale. Istruisci l'AI con i tuoi pensieri, la tua voce e i tuoi contenuti. 
                  Quando non ci sarai più, la tua AI vivrà per sempre e potrà far sentire ai tuoi cari la tua presenza.
                </p>
                <Button variant="outline-primary" className="group/btn mt-auto">
                  Inizia il mio Diario
                  <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform duration-300" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Rivivi un rapporto */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
            <Card className="bg-card border-border/40 shadow-card hover:shadow-elevated transition-all duration-500 cursor-pointer group h-full" onClick={() => choose("rivivi")}>
              <CardContent className="p-8 flex flex-col h-full">
                <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center mb-6 group-hover:bg-accent/15 transition-colors duration-300">
                  <Heart className="w-7 h-7 text-accent" />
                </div>
                <h2 className="font-serif text-xl font-bold text-foreground mb-3">Rivivi un rapporto</h2>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6 flex-1">
                  Hai perso una persona cara? Carica le sue informazioni, foto, vocali e profili social. 
                  L'AI ricreerà la sua personalità per una conversazione autentica e affettuosa.
                </p>
                <Button variant="outline-accent" className="group/btn mt-auto">
                  Crea il profilo
                  <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform duration-300" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
